import React, {FC, Key} from 'react';

import {computeIsDraftInReadMode} from '../../../../../../srcCommon/helpers/messageDraftHelpers';
import {makeConversationActionContext} from '../../../../../helpers/models/conversationHelpers';
import {isMessageVisuallyShared} from '../../../../../helpers/models/messageHelpers';
import {ActivityModel} from '../../../../../models/api/activityModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {DraftMessageModel} from '../../../../../models/api/messageModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {ViewerPanelConversationPageState} from '../../../../../store/panels/viewer/viewerPanelState';
import {TimelineVariationsEnum} from '../../../../../store/timelineVariations/timelineVariationSelectors';
import {MessageComposerContainer} from '../../messageComposer/messageComposerContainer';
import {MessageViewerContainer} from './messageViewerContainer';

/*
 * Types.
 */

export interface MessageDraftsProps {
  panelId: ViewerPanelId;
  userId: number;
  page: ViewerPanelConversationPageState;
  conversation: ConversationModel;
  activities: ReadonlyArray<ActivityModel>;
  replyingTeammates: ReadonlyArray<TeammateModel>;
  readonlyDraftMessageUids: ReadonlySet<string>;
  hasAutoShareDrafts: boolean;
  dontFocusComposer?: boolean;
  isSpam: boolean;
  focusKey?: Key;
  composeDraftMessages: ReadonlyArray<DraftMessageModel>;
  timelineVariation: TimelineVariationsEnum;
  isInComposerOnBottom?: boolean;
}

/*
 * Components.
 */

export const MessageDrafts: FC<MessageDraftsProps> = (props) => {
  const {panelId, userId, conversation, isSpam, page, focusKey, timelineVariation, isInComposerOnBottom} =
    props;

  return (
    <>
      {props.composeDraftMessages.map((draft) => {
        // Shared draft from other teammate OR readonly draft.
        const isDraftShared = isMessageVisuallyShared(draft, conversation, props.hasAutoShareDrafts);
        const isDraftInReadMode = computeIsDraftInReadMode(
          draft,
          userId,
          props.readonlyDraftMessageUids,
          isDraftShared,
        );
        if (isDraftInReadMode) {
          return (
            <MessageViewerContainer
              key={draft.uid}
              panelId={panelId}
              messageUid={draft.uid}
              cellsContext={page.cellsContext}
              namespace={conversation.namespace}
              conversationId={conversation.id}
              conversationActionContext={makeConversationActionContext(page.cellsContext, conversation)}
              isSpam={isSpam}
              isAuthorReplying={props.replyingTeammates.some((r) => r.id === draft.authorId)}
              isSharedDraft
              isFirstMessage={props.activities.length === 0}
              timelineVariation={timelineVariation}
              isInComposerOnBottom={isInComposerOnBottom}
            />
          );
        }

        // Composer draft.
        return (
          <MessageComposerContainer
            key={draft.uid}
            panelId={panelId}
            message={draft}
            cellsContext={page.cellsContext}
            dontFocusComposer={props.dontFocusComposer || focusKey !== draft.uid}
            conversation={conversation}
            isFirst={props.activities.length === 0}
            timelineVariation={timelineVariation}
          />
        );
      })}
    </>
  );
};
